import { useState, useEffect } from "react";
import { CheckCircle2, Link2, ShieldAlert, Loader2, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { uid } from "../../lib/uid";
import { formatINR, formatDate } from "../../lib/api";


// Inline form to tally a bank row as a PERSONAL transfer against a person's account.
function PersonalTallyForm({ txn, people, tallyPersonal }) {
  const [open, setOpen] = useState(false);
  const [personId, setPersonId] = useState("");
  const [kind, setKind] = useState("advance");
  const [note, setNote] = useState("");
  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
              className="w-full text-left px-2 py-1.5 hover:bg-[#F5F3EC] text-xs border-t border-[#E5E3DB] mt-1 text-[#1A1A18] font-medium"
              data-testid={`personal-tally-open-${txn.id}`}>
        Tally as personal transfer
      </button>
    );
  }
  const person = (people || []).find((p) => p.id === personId);
  return (
    <div className="border-t border-[#E5E3DB] mt-1 pt-2" data-testid={`personal-tally-form-${txn.id}`}>
      <div className="text-xs font-semibold text-[#68665E] px-1 pb-1">Personal transfer · {formatINR(txn.debit || txn.credit)}</div>
      <select value={personId} onChange={(e) => setPersonId(e.target.value)}
              className="input-field w-full mb-2 text-sm" data-testid={`personal-person-${txn.id}`}>
        <option value="">Select person…</option>
        {(people || []).map((p) => <option key={p.id} value={p.id}>{p.name}{p.role === "admin" ? " (admin)" : ""}</option>)}
      </select>
      <div className="flex gap-1 mb-2 px-0.5">
        {[["advance", "Advance / Loan"], ["draw", "Personal draw"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setKind(k)}
                  className={`text-[11px] px-2 py-0.5 rounded-full border ${kind === k ? "bg-[#C84B31] text-white border-[#C84B31]" : "border-[#E5E3DB] text-[#68665E] hover:bg-[#F5F3EC]"}`}
                  data-testid={`personal-kind-${k}-${txn.id}`}>
            {lbl}
          </button>
        ))}
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
             className="input-field w-full mb-2 text-sm" data-testid={`personal-note-${txn.id}`} />
      <div className="flex gap-2">
        <button onClick={() => tallyPersonal(txn, { person_id: personId, person_name: person?.name || "", kind, note })}
                disabled={!personId}
                className="btn-primary text-xs px-3 py-1.5 flex-1 disabled:opacity-50" data-testid={`personal-save-${txn.id}`}>
          Tally
        </button>
        <button onClick={() => setOpen(false)} className="text-xs px-3 py-1.5 border border-[#E5E3DB] hover:bg-[#F5F3EC]">Cancel</button>
      </div>
      <div className="text-[10px] text-[#8A8676] px-1 pt-1">Advance/Loan = owed back · Draw = personal. Kept out of business expenses.</div>
    </div>
  );
}

// Small inline form to tally a bank row to an LR / invoice number (even if it doesn't exist yet).
function TallyRefForm({ txn, tallyRef }) {
  const [type, setType] = useState("lr");
  const [num, setNum] = useState("");
  return (
    <div className="border-t border-[#E5E3DB] mt-1 pt-2" data-testid={`tally-ref-form-${txn.id}`}>
      <div className="text-xs font-semibold text-[#68665E] px-1 pb-1">Tally to an LR / Invoice number</div>
      <div className="flex gap-1 mb-1.5 px-1">
        {["lr", "invoice"].map((tp) => (
          <button key={tp} onClick={() => setType(tp)}
                  className={`text-[11px] px-2 py-0.5 rounded-full border ${type === tp ? "bg-[#C84B31] text-white border-[#C84B31]" : "border-[#E5E3DB] text-[#68665E] hover:bg-[#F5F3EC]"}`}
                  data-testid={`tally-ref-type-${tp}-${txn.id}`}>
            {tp === "lr" ? "LR" : "Invoice"}
          </button>
        ))}
      </div>
      <div className="flex gap-1">
        <input value={num} onChange={(e) => setNum(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") tallyRef(txn, type, num); }}
               placeholder={type === "lr" ? "e.g. LR-20260001" : "e.g. INV-0007"}
               className="input-field flex-1 text-sm font-mono" data-testid={`tally-ref-input-${txn.id}`} />
        <button onClick={() => tallyRef(txn, type, num)}
                className="btn-primary text-xs px-3 py-1.5" data-testid={`tally-ref-save-${txn.id}`}>Tally</button>
      </div>
      <div className="text-[10px] text-[#8A8676] px-1 pt-1">Not created yet? It'll auto-link when you make it.</div>
    </div>
  );
}

export function BankLedger({ loading, txns, openTally, setOpenTally, expenseForm, setExpenseForm, tally, tallyRef, untally, createExpense, categories, lrs, tallyPersonal, people }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const total = txns.length;
  useEffect(() => { setPage(0); }, [total, pageSize]);
  const size = pageSize === "all" ? Math.max(total, 1) : pageSize;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const safePage = Math.min(page, pageCount - 1);
  const startIdx = safePage * size;
  const paged = pageSize === "all" ? txns : txns.slice(startIdx, startIdx + size);
  return (
    <div className="mb-8" data-testid="bank-ledger">
      {!loading && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 text-xs text-[#68665E]" data-testid="bank-ledger-toolbar">
          <div>
            Showing <span className="font-semibold text-[#1A1A18]">{total === 0 ? 0 : startIdx + 1}–{Math.min(startIdx + size, total)}</span> of <span className="font-semibold text-[#1A1A18]">{total}</span>
          </div>
          <div className="flex items-center gap-1">
            <span>Show</span>
            {[50, 100, 200, "all"].map((s) => (
              <button key={s} onClick={() => setPageSize(s)}
                      className={`px-2 py-0.5 rounded-full border ${pageSize === s ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "border-[#E5E3DB] hover:bg-[#F5F3EC]"}`}
                      data-testid={`page-size-${s}`}>
                {s === "all" ? "All" : s}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="table-wrap">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="table-th">Account</th>
            <th className="table-th">Date</th>
            <th className="table-th">Description</th>
            <th className="table-th text-right">Debit</th>
            <th className="table-th text-right">Credit</th>
            <th className="table-th text-right">Balance</th>
            <th className="table-th">Tally</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7} className="table-td text-center py-8 text-[#68665E]"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
          ) : txns.length === 0 ? (
            <tr><td colSpan={7} className="table-td text-center py-10 text-[#68665E]">No transactions yet. Upload a bank statement to begin.</td></tr>
          ) : paged.map((t) => {
            const untallied = t.tally_status !== "tallied";
            return (
              <tr key={t.id}
                  className={`table-row ${t.tamper_flag ? "outline outline-1 outline-[#8A2526] bg-[#FBEDED]" : untallied ? "bg-[#FBEDED]/40" : ""}`}
                  data-testid={`txn-row-${t.id}`}>
                <td className="table-td whitespace-nowrap text-xs text-[#68665E]">{t.account_label || t.bank_name}</td>
                <td className="table-td whitespace-nowrap">{formatDate(t.date) || "—"}</td>
                <td className="table-td max-w-[280px]">
                  <div className="truncate" title={t.description}>{t.description || "—"}</div>
                  {t.tamper_flag && (
                    <div className="text-[11px] text-[#8A2526] font-medium mt-0.5 flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" /> {t.tamper_reason}
                    </div>
                  )}
                </td>
                <td className="table-td text-right font-medium text-[#8A2526]">
                  {t.debit ? formatINR(t.debit) : ""}
                  {t.debit > 0 && untallied && t.suggested_category && (
                    <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#C84B31]/80" data-testid={`suggested-category-${t.id}`}>
                      {t.suggested_category}
                    </div>
                  )}
                </td>
                <td className="table-td text-right font-medium text-[#2F6F4E]">{t.credit ? formatINR(t.credit) : ""}</td>
                <td className="table-td text-right text-[#68665E]">{t.balance == null ? "—" : formatINR(t.balance)}</td>
                <td className="table-td relative">
                  {t.tally_status === "tallied" ? (
                    <div className="flex items-center gap-2">
                      <span className="badge-success inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {t.matched_label || "Tallied"}</span>
                      <button onClick={() => untally(t)} className="text-xs text-[#8A2526] underline" data-testid={`untally-${t.id}`}>undo</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button onClick={() => setOpenTally(openTally === t.id ? null : t.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#E5E3DB] bg-white hover:bg-[#F5F3EC] transition-colors" data-testid={`tally-btn-${t.id}`}>
                        <Link2 className="w-3 h-3" /> Tally{t.suggestions?.length ? ` (${t.suggestions.length})` : ""}
                      </button>
                      {openTally === t.id && (
                        <div className="absolute z-20 right-2 top-full mt-1 w-80 bg-white border border-[#E5E3DB] shadow-lg p-2" data-testid={`tally-panel-${t.id}`}>
                          {expenseForm && expenseForm.txnId === t.id ? (
                            <div data-testid={`expense-form-${t.id}`}>
                              <div className="text-xs font-semibold text-[#68665E] px-1 pb-1">New expense · {formatINR(t.debit)}
                                {t.suggested_category && <span className="ml-1 text-[#C84B31]">· suggested: {t.suggested_category}</span>}
                              </div>
                              <select value={expenseForm.category}
                                      onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                                      className="input-field w-full mb-2 text-sm" data-testid={`expense-category-${t.id}`}>
                                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                              </select>
                              <input value={expenseForm.reason}
                                     onChange={(e) => setExpenseForm({ ...expenseForm, reason: e.target.value })}
                                     placeholder="Reason / note" className="input-field w-full mb-2 text-sm"
                                     data-testid={`expense-reason-${t.id}`} />
                              <datalist id="bank-lr-list">{(lrs || []).map((l) => <option key={l.id} value={l.lr_number} />)}</datalist>
                              <div className="mb-2" data-testid={`expense-splits-${t.id}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[11px] text-[#68665E]">Link / split across LRs (optional)</span>
                                  <div className="flex items-center gap-2">
                                    <button type="button" data-testid={`expense-even-split-${t.id}`}
                                            onClick={() => { const arr = (expenseForm.allocations || []); if (!arr.length) return; const each = Math.round((t.debit / arr.length) * 100) / 100; const na = arr.map((a, i) => ({ ...a, amount: i === arr.length - 1 ? Math.round((t.debit - each * (arr.length - 1)) * 100) / 100 : each })); setExpenseForm({ ...expenseForm, allocations: na }); }}
                                            className="text-[11px] text-[#68665E] font-medium">÷ Equally</button>
                                    <button type="button" data-testid={`expense-add-lr-${t.id}`}
                                            onClick={() => setExpenseForm({ ...expenseForm, allocations: [...(expenseForm.allocations || []), { _uid: uid(), lr_number: "", amount: 0 }] })}
                                            className="text-[11px] text-[#C84B31] font-medium">+ Add LR</button>
                                  </div>
                                </div>
                                {(expenseForm.allocations || []).map((a, i) => (
                                  <div key={a._uid ?? i} className="flex gap-1 mb-1 items-center">
                                    <input list="bank-lr-list" value={a.lr_number}
                                           onChange={(e) => { const arr = (expenseForm.allocations || []).slice(); arr[i] = { ...arr[i], lr_number: e.target.value }; setExpenseForm({ ...expenseForm, allocations: arr }); }}
                                           placeholder="LR number" className="input-field flex-1 text-xs font-mono" data-testid={`expense-alloc-lr-${t.id}-${i}`} />
                                    <input type="number" step="0.01" value={a.amount}
                                           onChange={(e) => { const arr = (expenseForm.allocations || []).slice(); arr[i] = { ...arr[i], amount: Number(e.target.value) }; setExpenseForm({ ...expenseForm, allocations: arr }); }}
                                           placeholder="₹" className="input-field w-20 text-xs" data-testid={`expense-alloc-amt-${t.id}-${i}`} />
                                    <button type="button" data-testid={`expense-alloc-remove-${t.id}-${i}`}
                                            onClick={() => setExpenseForm({ ...expenseForm, allocations: (expenseForm.allocations || []).filter((_, idx) => idx !== i) })}
                                            className="text-[#8A2526] text-sm px-1">×</button>
                                  </div>
                                ))}
                                {(expenseForm.allocations || []).length > 0 && (() => {
                                  const sum = (expenseForm.allocations || []).reduce((s, a) => s + Number(a.amount || 0), 0);
                                  const diff = Math.round((t.debit - sum) * 100) / 100;
                                  return <div className={`text-[10px] ${diff === 0 ? "text-[#2F6F4E]" : "text-[#8A6D3B]"}`} data-testid={`expense-alloc-summary-${t.id}`}>Allocated {formatINR(sum)} of {formatINR(t.debit)}{diff === 0 ? " ✓" : ` · ${formatINR(Math.abs(diff))} ${diff > 0 ? "left" : "over"}`}</div>;
                                })()}
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => createExpense(t)}
                                        className="btn-primary text-xs px-3 py-1.5 flex-1" data-testid={`expense-save-${t.id}`}>
                                  Create & Tally
                                </button>
                                <button onClick={() => setExpenseForm(null)}
                                        className="text-xs px-3 py-1.5 border border-[#E5E3DB] hover:bg-[#F5F3EC]">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="text-xs font-semibold text-[#68665E] px-1 pb-1">
                                {t.suggestions?.length ? "Suggested matches" : "No auto-suggestion"}
                              </div>
                              {(t.suggestions || []).map((s) => (
                                <button key={s.id}
                                        onClick={() => tally(t, { matched_type: s.type, matched_id: s.id, matched_label: s.label })}
                                        className="w-full text-left px-2 py-1.5 hover:bg-[#F5F3EC] text-xs" data-testid={`suggestion-${t.id}-${s.id}`}>
                                  <div className="font-medium text-[#1A1A18] truncate">{s.label}</div>
                                  <div className="text-[#68665E]">{s.type} · {formatDate(s.date)} · {formatINR(s.amount)}</div>
                                </button>
                              ))}
                              {t.debit > 0 && (
                                <button onClick={() => setExpenseForm({ txnId: t.id, category: t.suggested_category || "Misc", reason: t.description || "", lr_number: "", allocations: [{ _uid: uid(), lr_number: "", amount: t.debit }] })}
                                        className="w-full text-left px-2 py-1.5 hover:bg-[#F5F3EC] text-xs border-t border-[#E5E3DB] mt-1 text-[#C84B31] font-medium inline-flex items-center gap-1"
                                        data-testid={`create-expense-btn-${t.id}`}>
                                  <Plus className="w-3 h-3" /> Create expense from this row
                                </button>
                              )}
                              <button onClick={() => tally(t, { matched_type: "manual" })}
                                      className="w-full text-left px-2 py-1.5 hover:bg-[#F5F3EC] text-xs border-t border-[#E5E3DB] mt-1" data-testid={`manual-tally-${t.id}`}>
                                Mark tallied manually
                              </button>
                              <PersonalTallyForm txn={t} people={people} tallyPersonal={tallyPersonal} />
                              <TallyRefForm txn={t} tallyRef={tallyRef} />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {!loading && pageSize !== "all" && pageCount > 1 && (
        <div className="flex items-center justify-center gap-3 mt-3 text-xs" data-testid="bank-ledger-pager">
          <button disabled={safePage === 0} onClick={() => setPage(safePage - 1)}
                  className="inline-flex items-center gap-1 px-2 py-1 border border-[#E5E3DB] disabled:opacity-40 hover:bg-[#F5F3EC]" data-testid="pager-prev">
            <ChevronLeft className="w-3 h-3" /> Prev
          </button>
          <span className="text-[#68665E]">Page <span className="font-semibold text-[#1A1A18]">{safePage + 1}</span> of {pageCount}</span>
          <button disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}
                  className="inline-flex items-center gap-1 px-2 py-1 border border-[#E5E3DB] disabled:opacity-40 hover:bg-[#F5F3EC]" data-testid="pager-next">
            Next <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
